import { useCallback, useState } from 'react';
import { Button, Flex, NativeSelect, TextInput } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { COUNTRY_OPTIONS } from '../EidEasySigningModal.types';
import { StepBody } from './StepBody';
import { StepFooter } from './StepFooter';
import { StepHeader } from './StepHeader';
import { StepHeading, StepSubHeading } from './typography';

interface Props {
	onConfirm: (idcode: string, countryCode: string) => void;
	onGoBack: () => void;
	hideStepHeader?: boolean;
}

export const SmartIdInputStep = ({ onConfirm, onGoBack, hideStepHeader }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const [idcode, setIdcode] = useState('');
	const [countryCode, setCountryCode] = useState('EE');

	const handleConfirm = useCallback(() => {
		const trimmed = idcode.trim();
		if (!trimmed) {
			return;
		}
		onConfirm(trimmed, countryCode);
	}, [idcode, countryCode, onConfirm]);

	return (
		<Flex direction="column" w="100%" h="100%">
			{!hideStepHeader && <StepHeader onGoBack={onGoBack} />}
			<StepBody>
				<Flex direction="column" gap={4}>
					<StepHeading>{tCommonUi('eid_easy_signing.smart_id_title')}</StepHeading>
					<StepSubHeading>{tCommonUi('eid_easy_signing.smart_id_prompt')}</StepSubHeading>
				</Flex>
				<NativeSelect
					label={tCommonUi('eid_easy_signing.country_label')}
					data={COUNTRY_OPTIONS}
					value={countryCode}
					onChange={(event) => setCountryCode(event.currentTarget.value)}
				/>
				<TextInput
					label={tCommonUi('eid_easy_signing.identity_code_label')}
					placeholder={tCommonUi('eid_easy_signing.identity_code_placeholder')}
					value={idcode}
					onChange={(event) => setIdcode(event.currentTarget.value)}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							handleConfirm();
						}
					}}
					data-testid={TEST_ELEMENT_IDS.EID_EASY_SMART_ID_IDCODE_INPUT}
				/>
			</StepBody>
			<StepFooter>
				<Button
					variant="filled"
					fullWidth
					disabled={!idcode.trim()}
					onClick={handleConfirm}
					data-testid={TEST_ELEMENT_IDS.EID_EASY_SMART_ID_SIGN_BUTTON}
				>
					{tCommonUi('eid_easy_signing.sign')}
				</Button>
			</StepFooter>
		</Flex>
	);
};
